/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Component, Fragment } from 'react'
import { injectIntl } from 'react-intl'
import { Formik } from 'formik'

import { EuiCallOut, EuiCode, EuiForm, EuiLoadingSpinner } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { SamlSettings } from '@modules/cloud-api/v1/types'
import type { AsyncRequestState, RegionId } from '@modules/ui-types'
import history from '@modules/utils/history'
import Header from '@modules/cui/Header'

import { authenticationProvidersUrl } from '../../../../../../../lib/urlBuilder'
import { editSamlAuthProviderCrumbs } from '../../../../../../../lib/crumbBuilder'
import validateSamlForm from '../validateSamlForm'
import { messages } from '../../authProviderMessages'
import SamlProviderForm from '../SamlProviderForm'
import { formToSettings, settingsToForm } from '../requestMapping'

import type { WrappedComponentProps } from 'react-intl'

export interface Props extends WrappedComponentProps {
  fetchSamlSecurityRealm: () => void
  fetchSamlSecurityRealmRequest: AsyncRequestState
  samlSettings: SamlSettings | undefined
  onSubmit: (payload: SamlSettings) => Promise<any>
  updateSamlSecurityRealmRequest: AsyncRequestState
  regionId: RegionId
  realmId: string
  isPlanChangePending?: boolean
  isSecurityDeploymentDisabled?: boolean
}

class EditSamlProvider extends Component<Props> {
  componentDidMount() {
    this.props.fetchSamlSecurityRealm()
  }

  render() {
    const {
      intl: { formatMessage },
      regionId,
      realmId,
    } = this.props

    return (
      <Fragment>
        <Header
          name={formatMessage(messages.editSamlProvider)}
          breadcrumbs={editSamlAuthProviderCrumbs({ regionId, realmId })}
        />

        {this.renderContent()}
      </Fragment>
    )
  }

  renderContent() {
    const {
      intl,
      fetchSamlSecurityRealmRequest,
      samlSettings,
      regionId,
      realmId,
      onSubmit,
      updateSamlSecurityRealmRequest,
      isPlanChangePending = false,
      isSecurityDeploymentDisabled = false,
    } = this.props

    if (fetchSamlSecurityRealmRequest.inProgress) {
      return <EuiLoadingSpinner />
    }

    if (!samlSettings) {
      return (
        <EuiCallOut
          title={
            <FormattedMessage
              {...messages.noSuchSamlRealmId}
              values={{ realmId: <EuiCode>{realmId}</EuiCode> }}
            />
          }
        />
      )
    }

    return (
      <EuiForm>
        <Formik
          enableReinitialize={true}
          initialValues={settingsToForm(samlSettings)}
          validate={validateSamlForm(intl)}
          onSubmit={(values) =>
            onSubmit(formToSettings(values)).then(() =>
              history.push(authenticationProvidersUrl(regionId)),
            )
          }
        >
          {(props) => (
            <SamlProviderForm
              {...props}
              submitRequest={updateSamlSecurityRealmRequest}
              onCancel={() => history.push(authenticationProvidersUrl(regionId))}
              disabled={isPlanChangePending || isSecurityDeploymentDisabled}
              regionId={regionId}
              realmId={realmId}
            />
          )}
        </Formik>
      </EuiForm>
    )
  }
}

export default injectIntl(EditSamlProvider)
